import { useState, useEffect } from "react";
import { api } from "../api/api";

// Hook to fetch all banks for a specific company
export const useBanks = (companyId) => {
  const [banks, setBanks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchBanks = async () => {
    if (!companyId) return;
    try {
      setIsLoading(true);
      setError(null);
      const data = await api.getBanks(companyId);
      setBanks(data);
    } catch (err) {
      setError(err);
      console.error(`Error fetching banks for company ${companyId}:`, err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBanks();
  }, [companyId]);

  return { 
    banks, 
    isLoading, 
    error, 
    refetch: fetchBanks 
  };
};

// Hook to fetch transactions for a specific company
export const useBankTransactions = (
  companyId,
  bankAccountId = null,
  descriptionFilter = "",
) => {
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchTransactions = async () => {
    if (!companyId) {
      setTransactions([]);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const response = await api.getBankTransactions2(companyId, bankAccountId, descriptionFilter);
      const data = await response.json();

      setTransactions(data);
    } catch (err) {
      setError(err);
      console.error("Error fetching transactions:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [companyId, bankAccountId, descriptionFilter]);

  return {
    transactions,
    isLoading,
    error,
    refetch: fetchTransactions,
  };
};

// Hook to fetch cash flow data for a specific company
export const useBankCashFlow = (companyId, bankAccountId = null) => {
  const [cashFlowData, setCashFlowData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!companyId) {
      setCashFlowData([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    const fetchCashFlow = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await api.getBankCashFlow(companyId, bankAccountId);
        setCashFlowData(data);
      } catch (err) {
        setError(err);
        console.error(
          `Error fetching cash flow for company ${companyId}:`,
          err,
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchCashFlow();
  }, [companyId, bankAccountId]);

  return {
    cashFlowData,
    isLoading,
    error,
    refetch: () => {
      if (!companyId) return;
      setIsLoading(true);
      setError(null);
      api
        .getBankCashFlow(companyId, bankAccountId)
        .then(setCashFlowData)
        .catch(setError)
        .finally(() => setIsLoading(false));
    },
  };
};
